import React, { useMemo } from "react";
import styled from "styled-components";
import type { TimeseriesDayPoint } from "@/api/campaign";
import { seriesWeeks } from "../../utils/timeseriesChart";
import { formatDayMonth, formatWeekday, formatWeekdayDayMonth } from "./chartTheme";
import { DailyLineChart } from "./DailyLineChart";
import { dailyPeriod } from "./dailyPeriod";
import { PeriodNav } from "./PeriodNav";

/*
 * The phone chart's Weekly view: one week day by day, on the same chart as the Monthly view. The
 * arrows step through the weeks that have data; WeeklyTimeseriesChart owns the selected week. Tapping
 * a day shows its values in the tooltip.
 */

const Section = styled.section`
	margin-top: 12px;
`;

interface DailyWeekChartProps {
	/** Daily points, oldest first. */
	series: TimeseriesDayPoint[];
	/** Monday of the week to show; `null` shows the latest week. */
	weekStart: string | null;
	/** The arrows ask for the previous or next week (its Monday). */
	onWeekChange: (weekStart: string) => void;
	/** Today in the report's timezone: a week in progress ends there. */
	today: string;
}

export const DailyWeekChart: React.FC<DailyWeekChartProps> = ({ series, weekStart, onWeekChange, today }) => {
	const weeks = useMemo(() => seriesWeeks(series), [series]);
	const byDay = useMemo(() => new Map(series.map((point) => [point.date, point])), [series]);
	const selected = weeks.findIndex((candidate) => candidate.start === weekStart);
	const index = selected >= 0 ? selected : weeks.length - 1;
	const week = weeks[index];
	if (!week) return null;

	const days = week.days.map((day) => day.date);
	const period = dailyPeriod(days, byDay, today, formatWeekday, formatWeekdayDayMonth);

	return (
		<Section aria-label="Daily views and engagements">
			<PeriodNav
				title={`${formatDayMonth(days[0])} – ${formatDayMonth(days[6])}`}
				unit="week"
				onPrevious={() => onWeekChange(weeks[index - 1].start)}
				onNext={() => onWeekChange(weeks[index + 1].start)}
				hasPrevious={index > 0}
				hasNext={index < weeks.length - 1}
			/>
			<DailyLineChart {...period} maxTicks={8} />
		</Section>
	);
};
